import { Stack } from "@mui/material";
import { Button, Drawer, Input, SelectField } from "components";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  useGetAllCountries,
  useGetAllStateByCountryId,
  useGlobalStore,
  useUpdateMspBilling,
} from "controller";
import { memo, useCallback, useEffect, useMemo } from "react";
import toast from "react-hot-toast";

const schema = yup.object({
  billingCountry: yup.number().required("Billing country is required"),
  billingState: yup.number().nullable(),
  billingPostalCode: yup
    .string()
    .nullable()
    .max(10, "Zip code must be at most 10 characters"),
  billingAddressLine1: yup
    .string()
    .required("Billing address is required")
    .max(150, "Address line 1 is too long"),
  billingAddressLine2: yup
    .string()
    .nullable()
    .max(150, "Address line 2 is too long"),
});

interface AddressEditDrawerProps {
  open: boolean;
  onClose: () => void;
  subscriptionDetails: {
    billing_country?: number;
    billing_state?: number;
    billing_postal_code?: string;
    line1?: string;
    line2?: string;
  } | null;
  onSuccess: () => void;
}

export const AddressEditDrawer = memo(
  ({
    open,
    onClose,
    subscriptionDetails,
    onSuccess,
  }: AddressEditDrawerProps) => {
    const mspId = useGlobalStore((state) => state.mspId);
    const { data: countries } = useGetAllCountries();
    const { mutateAsync: updateBilling, isLoading: isUpdatingBilling } =
      useUpdateMspBilling();

    const {
      handleSubmit,
      control,
      reset,
      setValue: setFormValue,
      formState: { errors },
    } = useForm({
      defaultValues: {
        billingCountry: 0,
        billingState: 0,
        billingPostalCode: "",
        billingAddressLine1: "",
        billingAddressLine2: "",
      },
      resolver: yupResolver(schema),
    });

    const countryCode = useWatch({ control, name: "billingCountry" });
    const { data: states } = useGetAllStateByCountryId(`${countryCode}` || "");

    useEffect(() => {
      if (subscriptionDetails) {
        setFormValue(
          "billingCountry",
          subscriptionDetails.billing_country ?? 0,
        );
        setFormValue("billingState", subscriptionDetails.billing_state ?? 0);
        setFormValue(
          "billingPostalCode",
          subscriptionDetails.billing_postal_code ?? "",
        );
        setFormValue("billingAddressLine1", subscriptionDetails.line1 ?? "");
        setFormValue("billingAddressLine2", subscriptionDetails.line2 ?? "");
      }
    }, [subscriptionDetails, setFormValue]);

    const onSubmitHandler = useCallback(async () => {
      await handleSubmit(
        async ({
          billingAddressLine1,
          billingAddressLine2,
          billingCountry,
          billingPostalCode,
          billingState,
        }) => {
          await updateBilling({
            mspId: mspId || "",
            data: {
              billingAddressLine1,
              billingAddressLine2,
              billingCountry,
              billingZipCode: billingPostalCode,
              billingState,
            },
          });
          toast.success("Billing address updated successfully");
          reset(undefined, { keepValues: true, keepDirty: false });
          onSuccess();
          onClose();
        },
      )();
    }, [handleSubmit, updateBilling, mspId, reset, onSuccess, onClose]);

    const handleCancel = useCallback(() => {
      onClose();
    }, [onClose]);

    const footer = useMemo(
      () => (
        <Stack
          direction="row"
          spacing="16px"
          justifyContent="flex-end"
          p="16px"
        >
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onSubmitHandler}
            loading={isUpdatingBilling}
          >
            Save
          </Button>
        </Stack>
      ),
      [handleCancel, onSubmitHandler, isUpdatingBilling],
    );

    if (!open) return null;

    return (
      <Drawer
        open={open}
        title="Edit Billing Address"
        footer={footer}
        onClose={onClose}
      >
        <Stack sx={{ width: "500px", p: "24px", gap: "20px" }}>
          <Input
            required
            name="billingAddressLine1"
            label="Address line 1"
            control={control}
            error={!!errors.billingAddressLine1}
            errorMessage={errors.billingAddressLine1?.message}
            inputProps={{ maxLength: 150 }}
          />
          <Input
            name="billingAddressLine2"
            label="Address line 2"
            control={control}
            error={!!errors.billingAddressLine2}
            inputProps={{ maxLength: 150 }}
          />
          <SelectField
            required
            options={countries}
            labelMappingKey="name"
            valueMappingKey="id"
            name="billingCountry"
            label="Country"
            isSearchable
            keepMenuItemOriginalCasing
            control={control}
            error={!!errors.billingCountry}
            errorMessage={errors.billingCountry?.message}
          />
          <SelectField
            options={states}
            labelMappingKey="state_name"
            valueMappingKey="id"
            name="billingState"
            label="State"
            isSearchable
            keepMenuItemOriginalCasing
            control={control}
            error={!!errors.billingState}
          />
          <Input
            name="billingPostalCode"
            label="Zip code"
            control={control}
            error={!!errors.billingPostalCode}
            errorMessage={errors.billingPostalCode?.message}
          />
        </Stack>
      </Drawer>
    );
  },
);

AddressEditDrawer.displayName = "AddressEditDrawer";
